import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom'; 
import Header from '../components/Header';
import MobileHeader from '../components/MobileHeader';
import Footer from '../components/Footer';
import { fetchAllTrips } from '../functions/FetchTrips'; // Import fetchAllTrips function
import FloatingActionButton from '../components/home/FloatingActionButton';
import PopUpModal from '../components/PopUpModal'; // Add this import

function Search() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [destination, setDestination] = useState("");
  const [duration, setDuration] = useState("");
  const [trips, setTrips] = useState([]); // State to store trips from Firestore
  const [loading, setLoading] = useState(true); // Loading state
  const [error, setError] = useState(null); // Error state
  const [desktopPlaceholder, setDesktopPlaceholder] = useState(""); // Typing animation placeholder
  const [showModal, setShowModal] = useState(false); // Add state for popup modal

  // Scroll to top when the component is mounted
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Parse query parameters when component mounts or location changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const queryParam = params.get('q');
    if (queryParam) {
      setSearchQuery(queryParam);
    }
  }, [location]);

  // Fetch trips from Firestore
  useEffect(() => {
    const getTrips = async () => {
      try {
        setLoading(true);
        const tripsData = await fetchAllTrips();
        setTrips(tripsData);
        setError(null);
      } catch (err) {
        // console.error("Error fetching trips:", err);
        setError("Failed to load trips. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    getTrips();
  }, []);

  // Typing animation for desktop search placeholder
  useEffect(() => {
    const destinations = [
      'Ladakh',
      'Manali',
      'Spiti',
      'Romantic Getaways',
      'Kashmir',
      'Meghalaya',
      'Bali',
      'Dubai'
    ];
    let destIdx = 0;
    let charIdx = 0;
    let typing = true;
    let timeoutId;

    function type() {
      if (typing) {
        if (charIdx <= destinations[destIdx].length) {
          setDesktopPlaceholder(`Type for ${destinations[destIdx].slice(0, charIdx)}`);
          charIdx++;
          timeoutId = setTimeout(type, 90);
        } else {
          typing = false;
          timeoutId = setTimeout(type, 1200);
        }
      } else {
        if (charIdx > 0) {
          setDesktopPlaceholder(`Type for ${destinations[destIdx].slice(0, charIdx)}`);
          charIdx--;
          timeoutId = setTimeout(type, 40);
        } else {
          typing = true;
          destIdx = (destIdx + 1) % destinations.length;
          timeoutId = setTimeout(type, 500);
        }
      }
    }
    type();
    return () => clearTimeout(timeoutId);
  }, []);

  // Show popup modal after 8 seconds (like Home page)
  useEffect(() => {
    const timer = setTimeout(() => setShowModal(true), 8000);
    return () => clearTimeout(timer);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchQuery) params.set('q', searchQuery);
    navigate(`/search?${params.toString()}`);
  };

  const handleViewDetails = (tripId) => {
    navigate(`/itinerary/${tripId}`);
  };

  // Filter trips based on search criteria
  const filteredTrips = useMemo(() => {
    return trips.filter(trip => {
      // Exclude trips with status "Draft"
      if (trip.status === "Draft") {
        return false;
      }

      // Text search filter
      if (searchQuery && !trip.tripname?.toLowerCase().includes(searchQuery.toLowerCase()) &&
          !trip.location?.toLowerCase().includes(searchQuery.toLowerCase()) &&
          !trip.category?.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }

      // Destination filter
      if (destination && !trip.location?.toLowerCase().includes(destination.toLowerCase())) {
        return false;
      }

      // Duration filter
      if (duration && trip.duration) {
        const days = parseInt(trip.duration) || 0;
        if (duration === "1-3" && days > 3) return false;
        if (duration === "4-7" && (days < 4 || days > 7)) return false;
        if (duration === "8+" && days < 8) return false;
      }

      return true;
    });
  }, [searchQuery, destination, duration, trips]);

  return (
    <>
      <div className="d-none d-lg-block">
        <Header />
      </div>
      <MobileHeader />
      <div className="search-page py-5" style={{ background: 'linear-gradient(to bottom, #fffacd, #ffffff)' }}>
        <div className="container">
          <div className="row mb-4 justify-content-center">
            <div className="col-12 col-lg-8">
              <h1 className="text-center mb-4">Find Your Perfect Trip</h1>
              <div className="search-form-container">
                <form onSubmit={handleSearch}>
                  <div 
                    className="input-group input-group-lg shadow rounded-4 bg-white p-2"
                    style={{
                      border: '1px solid #f0f0f0',
                      boxShadow: '0 4px 24px rgba(0,0,0,0.07)',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <input 
                      type="text" 
                      className="form-control border-0 bg-light rounded-3 px-4 py-3"
                      placeholder={desktopPlaceholder || "Search by keywords (e.g., Spiti, Ladakh, Weekend)"}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      aria-label="Search by keywords"
                      style={{
                        fontSize: '1.15rem',
                        boxShadow: 'none',
                        outline: 'none'
                      }}
                    />
                    <button 
                      type="submit" 
                      className="btn text-white px-4 search-btn d-flex align-items-center justify-content-center"
                      style={{
                        background: 'linear-gradient(90deg, #FFA500 60%, #FFB347 100%)',
                        border: 'none',
                        borderRadius: '2rem',
                        fontWeight: 600,
                        fontSize: '1.15rem',
                        boxShadow: '0 2px 8px rgba(255,165,0,0.15)'
                      }}
                    >
                      <span className="d-none d-sm-inline">Search</span>
                      <span className="d-inline d-sm-none">
                        <i className="fas fa-search" style={{ fontSize: '1.3rem', verticalAlign: 'middle' }}></i>
                      </span>
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          
          <div className="row">
            <div className="col-12">
              <h2 className="mb-3">
                Available Trips
                {!loading && filteredTrips.length > 0 ? 
                  <span className="text-muted fs-6 ms-2">({filteredTrips.length} found)</span> : 
                  null
                }
              </h2>
            </div>
          </div>
          
          {/* Loading indicator */}
          {loading && (
            <div className="text-center my-5">
              <div className="spinner-border text-warning" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Loading trips...</p>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="alert alert-danger text-center">
              {error}
            </div>
          )}
          
          {/* Show message when no results found */}
          {!loading && !error && filteredTrips.length === 0 ? (
            <div className="alert alert-info text-center">
              No trips match your search criteria. Please try different filters.
            </div>
          ) : !loading && !error ? (
            <>
              {/* Mobile Trip Cards for mobile view - pass filtered trips */}
              <div className="mobile-trips d-lg-none">
                <div className="container">
                  <div className="row">
                    {filteredTrips.map(trip => (
                      <div className="col-6 mb-3" key={trip.id}>
                        <div
                          className="mobile-trip-card clickable-trip-card"
                          onClick={() => handleViewDetails(trip.id)}
                          role="button"
                          tabIndex={0}
                          onKeyPress={e => { if (e.key === 'Enter') handleViewDetails(trip.id); }}
                          style={{ cursor: 'pointer' }}
                        >
                          {trip.discount && <div className="trip-discount-badge">{trip.discount}</div>}
                          <div className="trip-image">
                            <img src={trip.cover_image_url || "/images/placeholder.jpg"} alt={trip.tripname} className="img-fluid" />
                            <div className="trip-overlay"></div>
                            <div className="trip-content">
                              <h3 
                                className="trip-title text-white" 
                                style={{ fontSize: '1rem' }} // Reduced font size for mobile
                              >
                                {trip.tripname}
                              </h3>
                              <div className="trip-details">
                                <div className="trip-detail">
                                  <i className="fas fa-calendar-alt"></i> {trip.days && trip.nights ? `${trip.days}D/${trip.nights}N` : "Duration N/A"}
                                </div>
                                <div className="trip-detail">
                                  <i className="fas fa-map-marker-alt"></i> {trip.location || "N/A"}
                                </div>
                              </div>
                              <div className="trip-price">
                                <span className="price">₹ {trip.display_price || "Price N/A"}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Desktop view for trip results - use filtered trips */}
              <div className="desktop-trips d-none d-lg-block">
                <div className="row">
                  {filteredTrips.map(trip => (
                    <div className="col-md-6 col-lg-3 mb-4" key={trip.id}>
                      <div
                        className="desktop-trip-card position-relative clickable-trip-card"
                        onClick={() => handleViewDetails(trip.id)}
                        role="button"
                        tabIndex={0}
                        onKeyPress={e => { if (e.key === 'Enter') handleViewDetails(trip.id); }}
                        style={{ cursor: 'pointer' }}
                      >
                        {trip.discount && (
                          <div className="trip-discount position-absolute top-0 end-0 m-3 px-2 py-1 bg-danger text-white rounded">
                            {trip.discount}
                          </div>
                        )}
                        <div className="trip-image">
                          <img src={trip.cover_image_url || "/images/placeholder.jpg"} className="img-fluid w-100" alt={trip.tripname} style={{ borderRadius: '8px', height: '300px', objectFit: 'cover' }} />
                          <div className="trip-overlay"></div>
                          <div className="trip-content position-absolute bottom-0 p-3 w-100">
                            <h4 className="trip-title text-white mb-2">{trip.tripname}</h4>
                            <div className="trip-details mb-2">
                              <div className="d-flex align-items-center mb-1 text-white">
                                <i className="fas fa-calendar-alt me-2"></i>
                                <small>{trip.days && trip.nights ? `${trip.days}D/${trip.nights}N` : "Duration N/A"}</small>
                              </div>
                              <div className="d-flex align-items-center text-white">
                                <i className="fas fa-map-marker-alt me-2"></i>
                                <small>{trip.location || "Location N/A"}</small>
                              </div>
                            </div>
                            <div className="d-flex justify-content-between align-items-center">
                              <div className="price fw-bold text-white">₹ {trip.display_price || "N/A"}</div>
                              <button
                                className="btn btn-sm btn-outline-light"
                                onClick={e => {
                                  e.stopPropagation();
                                  handleViewDetails(trip.id);
                                }}
                                tabIndex={0}
                              >
                                View Details
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : null}
        </div>
      </div>
      <FloatingActionButton />
      <Footer />
      <PopUpModal isOpen={showModal} onClose={() => setShowModal(false)} />
    </>
  );
}

export default Search;
